/**
 * Releases Hook
 * Manages Release CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthToken } from './use-auth';

// Types
export interface Release {
  id: string;
  projectId: string;
  version: string;
  title: string;
  description: string | null;
  releaseNotes: string | null;
  plannedDate: string | null;
  status: 'DRAFT' | 'READY' | 'APPROVED' | 'RELEASED' | 'ROLLED_BACK';
  createdAt: string;
  updatedAt: string;
  evidences?: Array<{ id: string; type: string; title: string; status: string }>;
  _count?: { evidences: number };
}

// List releases
export function useReleases(projectId: string, filters?: { status?: Release['status'] }) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['releases', projectId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      
      const response = await fetch(`/api/projects/${projectId}/releases?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch releases');
      const data = await response.json();
      return data.data as Release[];
    },
    enabled: !!projectId && !!token,
  });
}

// Get single release
export function useRelease(projectId: string, releaseId: string) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['release', projectId, releaseId],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/releases/${releaseId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch release');
      const data = await response.json();
      return data.data as Release;
    },
    enabled: !!projectId && !!releaseId && !!token,
  });
}

// Create release
export function useCreateRelease(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Partial<Release>) => {
      const response = await fetch(`/api/projects/${projectId}/releases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create release');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases', projectId] });
    },
  });
}

// Update release
export function useUpdateRelease(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ releaseId, data }: { releaseId: string; data: Partial<Release> }) => {
      const response = await fetch(`/api/projects/${projectId}/releases/${releaseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update release');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['releases', projectId] });
      queryClient.invalidateQueries({ queryKey: ['release', projectId, variables.releaseId] });
    },
  });
}

// Update release status
export function useUpdateReleaseStatus(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ releaseId, status }: { releaseId: string; status: Release['status'] }) => {
      const response = await fetch(`/api/projects/${projectId}/releases/${releaseId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update release status');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['releases', projectId] });
      queryClient.invalidateQueries({ queryKey: ['release', projectId, variables.releaseId] });
    },
  });
}

// Link evidence
export function useLinkEvidence(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ releaseId, evidenceId }: { releaseId: string; evidenceId: string }) => {
      const response = await fetch(`/api/projects/${projectId}/releases/${releaseId}/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ evidenceId }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to link evidence');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['releases', projectId] });
      queryClient.invalidateQueries({ queryKey: ['release', projectId, variables.releaseId] });
    },
  });
}

// Delete release
export function useDeleteRelease(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (releaseId: string) => {
      const response = await fetch(`/api/projects/${projectId}/releases/${releaseId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete release');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases', projectId] });
    },
  });
}
